/** ONE Order fulfilment: serial picker per line, dispatch, and
 *  delivery confirmation with signed delivery-note upload. */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Truck, PackageCheck, Barcode, FileCheck2, ScanBarcode, CircleDot } from "lucide-react";

const TL_TONE = {
  CREATED: "bg-slate-400", SERIALS_ALLOCATED: "bg-indigo-500",
  IN_FULFILLMENT: "bg-sky-500", READY_TO_SHIP: "bg-sky-600",
  SHIPPED: "bg-amber-500", DELIVERED: "bg-emerald-600",
  INVOICED: "bg-violet-600", CLOSED: "bg-slate-600", CANCELLED: "bg-rose-500",
};

export function OrderTimeline({ order }) {
  const events = [
    { status: "CREATED", at: order?.created_at, by: order?.created_by, note: "Order created" },
    ...(order?.status_history || []),
  ].filter((e) => e.at).sort((a, b) => String(a.at).localeCompare(String(b.at)));
  if (events.length === 0) return null;
  return (
    <div className="mt-3" data-testid="order-timeline">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Order journey</div>
      <div className="relative pl-4 border-l-2 border-slate-200 space-y-2.5">
        {events.map((e, i) => (
          <div key={i} className="relative" data-testid={`timeline-event-${e.status}-${i}`}>
            <span className={`absolute -left-[21px] top-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white ${TL_TONE[e.status] || "bg-slate-300"}`} />
            <div className="text-[11px]">
              <span className="font-semibold text-slate-800">{(e.status || "").replace(/_/g, " ")}</span>
              <span className="text-slate-400 ml-2">{e.at ? new Date(e.at).toLocaleString() : ""}</span>
              {e.by && <span className="text-slate-400 ml-2">· {e.by}</span>}
            </div>
            {e.note && <div className="text-[10px] text-slate-500">{e.note}</div>}
          </div>))}
      </div>
    </div>
  );
}

function SerialPicker({ orderId, item, onDone }) {
  const [opts, setOpts] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/one-orders/${orderId}/serial-options`, { params: { product_id: item.product_id } })
      .then(({ data }) => {
        setOpts(data.items || []);
        setSel(new Set((data.items || []).filter((u) => u.allocated).map((u) => u.id)));
      }).catch(() => setOpts([]));
  }, [orderId, item.product_id]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/one-orders/${orderId}/allocate-serials`,
        { product_id: item.product_id, unit_ids: [...sel] });
      toast.success(`${data.allocated} serial(s) allocated${data.released ? `, ${data.released} released` : ""}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Allocation failed"); }
    finally { setBusy(false); }
  };

  if (opts === null) return <div className="text-[11px] text-slate-400 p-2">Loading stock…</div>;
  const scan = (raw) => {
    const code = raw.trim().toLowerCase();
    if (!code) return;
    const hit = opts.find((u) =>
      (u.serial || "").toLowerCase() === code || (u.service_tag || "").toLowerCase() === code);
    if (!hit) return toast.error(`No available unit with serial "${raw.trim()}"`);
    const n = new Set(sel);
    if (n.has(hit.id)) { n.delete(hit.id); toast.info(`${hit.serial} removed`); }
    else if (n.size >= (item.quantity || 999)) return toast.warning(`Line quantity is ${item.quantity}`);
    else { n.add(hit.id); toast.success(`${hit.serial} added (${n.size}/${item.quantity})`); }
    setSel(n);
  };
  return (
    <div className="border border-indigo-200 bg-indigo-50/40 rounded-md p-2.5 mt-1" data-testid={`serial-picker-${item.product_id}`}>
      <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold mb-1.5">
        Pick serials — {item.product_name} (qty {item.quantity})</div>
      {opts.length > 0 && (
        <div className="mb-2 flex items-center gap-1.5">
          <ScanBarcode className="h-3.5 w-3.5 text-indigo-600" />
          <input autoFocus placeholder="Scan barcode / type serial + Enter"
            data-testid="serial-scan-input"
            onKeyDown={(e) => { if (e.key === "Enter") { scan(e.target.value); e.target.value = ""; } }}
            className="h-8 px-2 text-[11px] font-mono border border-indigo-300 rounded w-64 bg-white" />
          <span className="text-[10px] text-slate-500">Scanner or manual entry — Enter toggles</span>
        </div>)}
      {opts.length === 0 && <div className="text-[11px] text-slate-500">No serialised stock available for this product.</div>}
      <div className="flex flex-wrap gap-1.5 max-h-36 overflow-auto">
        {opts.map((u) => (
          <button key={u.id}
            onClick={() => { const n = new Set(sel);
              n.has(u.id) ? n.delete(u.id)
                : (n.size < (item.quantity || 999) ? n.add(u.id)
                   : toast.info(`Line quantity is ${item.quantity}`));
              setSel(n); }}
            data-testid={`serial-opt-${u.serial}`}
            className={`text-[10px] font-mono px-2 py-1 rounded border ${sel.has(u.id)
              ? "bg-indigo-700 text-white border-indigo-700"
              : "bg-white text-slate-700 border-slate-300"}`}>
            {u.serial || u.service_tag}{u.condition ? ` · ${u.condition}` : ""}</button>))}
      </div>
      {opts.length > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[10px] text-slate-500">{sel.size}/{item.quantity} selected</span>
          <button onClick={save} disabled={busy} data-testid="serial-picker-save"
            className="ml-auto h-7 px-3 text-[11px] bg-indigo-700 text-white rounded disabled:opacity-50">
            Save allocation</button>
        </div>)}
    </div>
  );
}

export default function FulfilmentPanel({ order, onChanged }) {
  const [pickFor, setPickFor] = useState(null);
  const [receivedBy, setReceivedBy] = useState("");
  const [courier, setCourier] = useState("");
  const [awb, setAwb] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const status = order?.status;
  const serialisable = (order?.items || []).filter((it) => it.product_id);

  const dispatch = async () => {
    setBusy(true);
    try {
      await api.post(`/one-orders/${order.id}/dispatch`, {
        courier: courier || undefined, awb: awb || undefined });
      toast.success("Order marked dispatched — customer notified by email");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const confirmDelivery = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Attach the signed delivery note first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("received_by", receivedBy);
      const { data } = await api.post(`/one-orders/${order.id}/delivery-confirmation`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Delivered — signed DN filed${data.email?.sent ? ", customer notified" : ""}`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const viewSignedDn = async () => {
    try {
      const res = await api.get(`/one-orders/${order.id}/signed-delivery-note`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open signed delivery note"); }
  };

  return (
    <div className="mt-4 border border-slate-200 rounded-md p-3" data-testid="fulfilment-panel">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
        <Truck className="h-3.5 w-3.5" /> Fulfilment</div>

      {serialisable.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-1.5">
            {serialisable.map((it) => (
              <button key={it.product_id}
                onClick={() => setPickFor(pickFor === it.product_id ? null : it.product_id)}
                data-testid={`pick-serials-btn-${it.product_id}`}
                className="h-7 px-2.5 text-[11px] border border-indigo-300 text-indigo-700 rounded inline-flex items-center gap-1 bg-white">
                <Barcode className="h-3 w-3" /> Serials — {(it.product_name || "").slice(0, 28)}</button>))}
          </div>
          {pickFor && (
            <SerialPicker orderId={order.id}
              item={serialisable.find((i) => i.product_id === pickFor)}
              onDone={() => { setPickFor(null); onChanged(); }} />)}
        </div>)}

      <div className="flex items-center gap-2 flex-wrap">
        {!["SHIPPED", "DELIVERED", "INVOICED", "CLOSED", "CANCELLED"].includes(status) && (
          <span className="inline-flex items-end gap-2 flex-wrap">
            <input value={courier} onChange={(e) => setCourier(e.target.value)}
              placeholder="Courier" data-testid="dispatch-courier"
              className="h-8 px-2 text-[11px] border border-slate-300 rounded w-28" />
            <input value={awb} onChange={(e) => setAwb(e.target.value)}
              placeholder="AWB / tracking #" data-testid="dispatch-awb"
              className="h-8 px-2 text-[11px] border border-slate-300 rounded w-36" />
            <button onClick={dispatch} disabled={busy} data-testid="mark-dispatched-btn"
              className="h-8 px-3 text-[11px] bg-slate-900 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-50">
              <Truck className="h-3 w-3" /> Mark Dispatched</button>
          </span>)}
        {order?.signed_delivery_note ? (
          <button onClick={viewSignedDn} data-testid="view-signed-dn-btn"
            className="h-8 px-3 text-[11px] border border-emerald-600 text-emerald-800 rounded inline-flex items-center gap-1.5">
            <FileCheck2 className="h-3 w-3" /> View signed DN
            <span className="text-slate-400">({order.signed_delivery_note.received_by || "received"})</span></button>
        ) : status !== "CANCELLED" && (
          <span className="inline-flex items-end gap-2 flex-wrap">
            <input type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png"
              data-testid="signed-dn-file"
              className="text-[11px] file:h-8 file:px-2 file:text-[11px] file:border file:border-slate-300 file:rounded file:bg-white" />
            <input value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)}
              placeholder="Received by" data-testid="signed-dn-received-by"
              className="h-8 px-2 text-[11px] border border-slate-300 rounded w-32" />
            <button onClick={confirmDelivery} disabled={busy} data-testid="confirm-delivery-btn"
              className="h-8 px-3 text-[11px] bg-emerald-700 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-50">
              <PackageCheck className="h-3 w-3" /> Mark Delivered + File DN</button>
          </span>)}
      </div>

      <OrderTimeline order={order} />
    </div>
  );
}
